import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { getHeaders } from '../../utility/getHeader';
import Layout from '../../layout/Layout';
import Header from '../../components/Header';
import { FaChartLine, FaDownload, FaFilter, FaUniversity, FaArrowUp, FaArrowDown, FaFilePdf } from 'react-icons/fa';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default function BankReports() {
    const [bankAccounts, setBankAccounts] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [summary, setSummary] = useState({});
    const [companyProfile, setCompanyProfile] = useState({});
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        bank_account_id: '',
        start_date: '',
        end_date: '',
        transaction_type: '',
        transaction_category: ''
    });

    const transactionTypes = ['', 'Credit', 'Debit'];
    const transactionCategories = ['', 'Sales', 'Purchase', 'Payment_Received', 'Payment_Made', 'Transfer', 'Adjustment', 'Opening_Balance'];

    useEffect(() => {
        fetchBankAccounts();
        fetchTransactions();
        fetchSummary();
        fetchCompanyProfile();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchBankAccounts = async () => {
        try {
            const response = await axios.get('/bank-accounts', getHeaders());
            setBankAccounts(response.data.data || []);
        } catch (error) {
            console.error('Error fetching bank accounts:', error);
        }
    };

    const fetchTransactions = async () => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams(filters);
            const response = await axios.get(
                `/bank-transactions?${queryParams}`, 
                getHeaders()
            );
            setTransactions(response.data.data || []);
        } catch (error) {
            console.error('Error fetching transactions:', error);
            toast.error('Failed to fetch transactions');
        }
        setLoading(false);
    };

    const fetchSummary = async () => {
        try {
            const response = await axios.get(
                '/bank-summary', 
                getHeaders()
            );
            setSummary(response.data.data || {});
        } catch (error) {
            console.error('Error fetching summary:', error);
        }
    };

    const fetchCompanyProfile = async () => {
        try {
            const response = await axios.get('/company-profile', getHeaders());
            if (response.data.success && response.data.data.length > 0) {
                setCompanyProfile(response.data.data[0]);
            }
        } catch (error) {
            console.error('Error fetching company profile:', error);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const applyFilters = () => {
        fetchTransactions();
    };

    const resetFilters = () => {
        setFilters({
            bank_account_id: '',
            start_date: '',
            end_date: '',
            transaction_type: '',
            transaction_category: ''
        });
        setTimeout(() => fetchTransactions(), 100);
    };

    const exportToCSV = () => {
        if (transactions.length === 0) {
            toast.warning('No data to export');
            return;
        }

        const headers = ['Date', 'Account', 'Type', 'Category', 'Description', 'Amount', 'Balance After'];
        const csvData = transactions.map(txn => [
            new Date(txn.transaction_date).toLocaleDateString(),
            txn.account_name,
            txn.transaction_type,
            txn.transaction_category,
            txn.description || '',
            txn.amount,
            txn.balance_after
        ]);

        const csvContent = [headers, ...csvData]
            .map(row => row.map(field => `"${field}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `bank_transactions_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const exportToPDF = () => {
        if (transactions.length === 0) {
            toast.warning('No data to export');
            return;
        }

        const doc = new jsPDF();
        
        // Add company header
        let currentY = 15;
        
        doc.setFontSize(18);
        doc.setFont(undefined, 'bold');
        if (companyProfile.name) {
            doc.text(companyProfile.name, 14, currentY);
            currentY += 8;
        }
        
        doc.setFontSize(12);
        doc.setFont(undefined, 'normal');
        if (companyProfile.address) {
            doc.text(`Address: ${companyProfile.address}`, 14, currentY);
            currentY += 5;
        }
        if (companyProfile.phone_number) {
            doc.text(`Phone: ${companyProfile.phone_number}`, 14, currentY);
            currentY += 5;
        }
        if (companyProfile.email) {
            doc.text(`Email: ${companyProfile.email}`, 14, currentY);
            currentY += 8;
        }
        
        doc.setFontSize(16);
        doc.setFont(undefined, 'bold');
        doc.text('Bank Transactions Report', 14, currentY);
        currentY += 6;
        
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(`Generated on: ${new Date().toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit' 
        })}`, 14, currentY);
        currentY += 8;

        const tableColumn = [
            'Date', 
            'Account', 
            'Type', 
            'Category', 
            'Description', 
            'Amount', 
            'Balance After'
        ];
        const tableRows = [];

        let totalCredits = 0;
        let totalDebits = 0;

        transactions.forEach(txn => {
            const amount = parseFloat(txn.amount) || 0;
            
            if (txn.transaction_type === 'Credit') {
                totalCredits += amount;
            } else {
                totalDebits += amount;
            }

            tableRows.push([
                formatDate(txn.transaction_date),
                (txn.account_name || 'N/A').substring(0, 15),
                txn.transaction_type,
                (txn.transaction_category || 'N/A').substring(0, 12),
                (txn.description || '').substring(0, 25),
                `${txn.transaction_type === 'Credit' ? '+' : '-'}${formatCurrency(amount)}`,
                formatCurrency(txn.balance_after)
            ]);
        });

        // Add summary rows
        tableRows.push([
            { content: '', colSpan: 4, styles: { fillColor: [240, 240, 240] } },
            { content: 'Summary', styles: { halign: 'center', fontStyle: 'bold', fillColor: [240, 240, 240] } },
            { content: '', colSpan: 2, styles: { fillColor: [240, 240, 240] } }
        ]);
        
        tableRows.push([
            { content: '', colSpan: 4 },
            { content: 'Total Credits:', styles: { halign: 'right', fontStyle: 'bold' } },
            { content: `+${formatCurrency(totalCredits)}`, styles: { fontStyle: 'bold', textColor: [76, 175, 80] } },
            { content: '', styles: {} }
        ]);
        
        tableRows.push([
            { content: '', colSpan: 4 },
            { content: 'Total Debits:', styles: { halign: 'right', fontStyle: 'bold' } },
            { content: `-${formatCurrency(totalDebits)}`, styles: { fontStyle: 'bold', textColor: [244, 67, 54] } },
            { content: '', styles: {} }
        ]);
        
        tableRows.push([
            { content: '', colSpan: 4 },
            { content: 'Net Balance:', styles: { halign: 'right', fontStyle: 'bold' } },
            { content: formatCurrency(summary.total_balance || 0), styles: { fontStyle: 'bold', textColor: [33, 150, 243] } },
            { content: '', styles: {} }
        ]);

        doc.autoTable({
            startY: currentY,
            head: [tableColumn],
            body: tableRows,
            styles: { 
                fontSize: 8,
                cellPadding: 2
            },
            headStyles: {
                fillColor: [33, 150, 243],
                textColor: [255, 255, 255],
                fontStyle: 'bold'
            },
            columnStyles: {
                0: { cellWidth: 20 }, // Date
                1: { cellWidth: 25 }, // Account
                2: { cellWidth: 15 }, // Type
                3: { cellWidth: 20 }, // Category
                4: { cellWidth: 35 }, // Description
                5: { cellWidth: 25, halign: 'right' }, // Amount
                6: { cellWidth: 25, halign: 'right' } // Balance After
            },
            alternateRowStyles: {
                fillColor: [248, 249, 250]
            }
        });

        const filename = `Bank_Transactions_${companyProfile.name ? companyProfile.name.replace(/\s+/g, '_') : 'StoreHub'}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(filename);
        
        toast.success('PDF report downloaded successfully!');
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount) + ' THB';
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <Layout>
            <Header title="Bank Reports & Transactions" />
            <div className="container-fluid p-4">
                {/* Summary Cards */}
                <div className="row mb-4">
                    <div className="col-md-3 mb-3">
                        <div className="card bg-primary text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h6 className="card-title">Total Balance</h6>
                                        <h4>{formatCurrency(summary.total_balance || 0)}</h4>
                                    </div>
                                    <FaUniversity size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 mb-3">
                        <div className="card bg-success text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h6 className="card-title">Total Credits</h6>
                                        <h4>{formatCurrency(summary.total_credits || 0)}</h4>
                                    </div>
                                    <FaArrowUp size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 mb-3">
                        <div className="card bg-danger text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h6 className="card-title">Total Debits</h6>
                                        <h4>{formatCurrency(summary.total_debits || 0)}</h4>
                                    </div>
                                    <FaArrowDown size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 mb-3">
                        <div className="card bg-info text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h6 className="card-title">Transactions</h6>
                                        <h4>{transactions.length}</h4>
                                    </div>
                                    <FaChartLine size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="card mb-4">
                    <div className="card-header">
                        <h5 className="card-title mb-0">
                            <FaFilter className="me-2" />
                            Filters
                        </h5>
                    </div>
                    <div className="card-body">
                        <div className="row">
                            <div className="col-md-2 mb-3">
                                <label className="form-label">Bank Account</label>
                                <select
                                    className="form-control"
                                    name="bank_account_id"
                                    value={filters.bank_account_id}
                                    onChange={handleFilterChange}
                                >
                                    <option value="">All Accounts</option>
                                    {bankAccounts.map(account => (
                                        <option key={account.id} value={account.id}>
                                            {account.account_name} ({account.bank_name})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-2 mb-3">
                                <label className="form-label">Start Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    name="start_date"
                                    value={filters.start_date}
                                    onChange={handleFilterChange}
                                />
                            </div>
                            <div className="col-md-2 mb-3">
                                <label className="form-label">End Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    name="end_date"
                                    value={filters.end_date}
                                    onChange={handleFilterChange}
                                />
                            </div>
                            <div className="col-md-2 mb-3">
                                <label className="form-label">Type</label>
                                <select
                                    className="form-control"
                                    name="transaction_type"
                                    value={filters.transaction_type}
                                    onChange={handleFilterChange}
                                >
                                    <option value="">All Types</option>
                                    {transactionTypes.filter(t => t).map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-2 mb-3">
                                <label className="form-label">Category</label>
                                <select
                                    className="form-control"
                                    name="transaction_category"
                                    value={filters.transaction_category}
                                    onChange={handleFilterChange}
                                >
                                    <option value="">All Categories</option>
                                    {transactionCategories.filter(c => c).map(category => (
                                        <option key={category} value={category}>
                                            {category.replace('_', ' ')}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-2 mb-3 d-flex align-items-end">
                                <div className="btn-group w-100">
                                    <button className="btn btn-primary" onClick={applyFilters}>
                                        Apply
                                    </button>
                                    <button className="btn btn-secondary" onClick={resetFilters}>
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Transactions Table */}
                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h5 className="card-title mb-0">
                            <FaChartLine className="me-2" />
                            Bank Transactions
                        </h5>
                        <div className="d-flex gap-2">
                            <button className="btn btn-danger" onClick={exportToPDF}>
                                <FaFilePdf className="me-2" />
                                Export PDF
                            </button>
                            <button className="btn btn-success" onClick={exportToCSV}>
                                <FaDownload className="me-2" />
                                Export CSV
                            </button>
                        </div>
                    </div>
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : transactions.length === 0 ? (
                            <div className="text-center py-4">
                                <p className="text-muted mb-0">No transactions found for the selected criteria.</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-striped table-hover">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>Date</th>
                                            <th>Account</th>
                                            <th>Type</th>
                                            <th>Category</th>
                                            <th>Description</th>
                                            <th>Reference</th>
                                            <th>Amount</th>
                                            <th>Balance After</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transactions.map((transaction) => (
                                            <tr key={transaction.id}>
                                                <td>{formatDate(transaction.transaction_date)}</td>
                                                <td>
                                                    <div>
                                                        <div className="fw-semibold">{transaction.account_name}</div>
                                                        <small className="text-muted">{transaction.bank_name}</small>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`badge ${transaction.transaction_type === 'Credit' ? 'bg-success' : 'bg-danger'}`}>
                                                        {transaction.transaction_type === 'Credit' ? 
                                                            <><FaArrowUp className="me-1" />Credit</> : 
                                                            <><FaArrowDown className="me-1" />Debit</>
                                                        }
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="badge bg-secondary">
                                                        {transaction.transaction_category.replace('_', ' ')}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div style={{ maxWidth: '200px' }}>
                                                        {transaction.description}
                                                    </div>
                                                </td>
                                                <td>
                                                    {transaction.reference_number && (
                                                        <span className="badge bg-info">
                                                            {transaction.reference_number}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className={`fw-semibold ${transaction.transaction_type === 'Credit' ? 'text-success' : 'text-danger'}`}>
                                                    {transaction.transaction_type === 'Credit' ? '+' : '-'}
                                                    {formatCurrency(transaction.amount)}
                                                </td>
                                                <td className="fw-semibold">
                                                    {formatCurrency(transaction.balance_after)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ToastContainer />
        </Layout>
    );
}